import classNames from 'classnames';
import NextLink, { type LinkProps as NextLinkProps } from 'next/link';
import { usePathname } from 'next/navigation';
import { type AnchorHTMLAttributes, forwardRef, type ReactNode } from 'react';

import './link.scss';

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof NextLinkProps> &
  NextLinkProps & {
    children: ReactNode;
  };

const isAppPath = (path: string): boolean => {
  return (
    path === '/' ||
    path === '/ua' ||
    path === '/ru' ||
    path === '/en' ||
    path === '/admin' ||
    path.startsWith('/admin/') ||
    /^\/(ua|ru|en)\/admin(?:\/|$)/.test(path) ||
    path === '/platform' ||
    path.startsWith('/platform/') ||
    /^\/(ua|ru|en)\/platform(?:\/|$)/.test(path) ||
    path === '/courses' ||
    path.startsWith('/courses/') ||
    /^\/(ua|ru|en)\/courses(?:\/|$)/.test(path) ||
    path === '/posts' ||
    path.startsWith('/posts/') ||
    /^\/(ua|ru|en)\/posts(?:\/|$)/.test(path) ||
    path === '/categories' ||
    path.startsWith('/categories/') ||
    /^\/(ua|ru|en)\/categories(?:\/|$)/.test(path)
  );
};

const getPathnameFromHref = (href: LinkProps['href']): string => {
  if (typeof href === 'string') {
    return href.split('?')[0] ?? href;
  }

  return href.pathname ?? '';
};

const shouldDisablePrefetch = ({
  currentPathname,
  hrefPathname,
}: {
  currentPathname: string | null;
  hrefPathname: string;
}): boolean => {
  const current = currentPathname ?? '';

  const currentIsApp = isAppPath(current);
  const targetIsApp = isAppPath(hrefPathname);

  // –í–∞–∂–Ω–æ: –µ—Å–ª–∏ –ø–µ—Ä–µ—Ö–æ–¥–∏–º –º–µ–∂–¥—É —Ä–∞–∑–Ω—ã–º–∏ Next-–ø—Ä–∏–ª–æ–∂–µ–Ω–∏—è–º–∏,
  // –Ω–µ –¥–∞—ë–º Next.js –¥–µ–ª–∞—Ç—å RSC-prefetch.
  if (currentIsApp !== targetIsApp) {
    return true;
  }

  return false;
};

const Link = forwardRef<HTMLAnchorElement, LinkProps>(({ href, children, prefetch, className, ...props }, ref) => {
  const pathname = usePathname();
  const hrefPathname = getPathnameFromHref(href);

  const publicPath =
    /^(?:\/(?:ua|ru|en))?\/(?:posts|courses|categories)(?:\/|$)/.test(hrefPathname) &&
    !/\/courses\/[^/]+\/start-course(?:\/|$)/.test(hrefPathname);
  if (publicPath && typeof href === 'string') {
    const anchorProps = { ...props } as Record<string, unknown>;
    for (const key of ['replace', 'scroll', 'shallow', 'locale', 'legacyBehavior', 'passHref', 'onNavigate'])
      delete anchorProps[key];
    return (
      <a ref={ref} href={href} {...anchorProps} className={classNames('link', className)}>
        {children}
      </a>
    );
  }

  const resolvedPrefetch =
    prefetch ?? (shouldDisablePrefetch({ currentPathname: pathname, hrefPathname }) ? false : undefined);

  return href ? (
    <NextLink ref={ref} href={href} prefetch={resolvedPrefetch} {...props} className={classNames('link', className)}>
      {children}
    </NextLink>
  ) : (
    <span>{children}</span>
  );
});

Link.displayName = 'Link';

export default Link;
